import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { HR } from '../components/HR'
import { Button } from '../components/Button'
import { Tag } from '../components/Tag'
import { api, type ApiEvent, type ApiTicket } from '../services/api'
import styles from './TicketsScreen.module.css'

export function TicketsScreen() {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [event, setEvent] = useState<ApiEvent | null>(null)
  const [myTickets, setMyTickets] = useState<ApiTicket[]>([])
  const [feePerTicket, setFeePerTicket] = useState<string>('0.00')
  const [qty, setQty] = useState(1)
  const [purchasing, setPurchasing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [actionError, setActionError] = useState<string | null>(null)

  const isDoorStaff = user?.role === 'admin' || user?.role === 'volunteer'

  useEffect(() => {
    if (!eventId) {
      setLoading(false)
      return
    }
    Promise.all([
      api.events.get(Number(eventId)),
      api.tickets.list(Number(eventId)),
      api.settings.communityFee().catch(() => ({ fee: '0.00' })),
    ]).then(([evt, tickets, feeData]) => {
      setEvent(evt)
      setMyTickets(tickets)
      setFeePerTicket(feeData.fee)
      setLoading(false)
    }).catch(() => {
      setEvent(null)
      setMyTickets([])
      setFeePerTicket('0.00')
      setLoading(false)
    })
  }, [eventId])

  const ticketPrice = event?.price || 'Free'

  const handlePurchase = async () => {
    setPurchasing(true)
    try {
      const res = await api.tickets.checkout(Number(eventId), qty)
      window.location.href = res.checkoutUrl
    } catch {
      setPurchasing(false)
      setActionError('Failed to start checkout. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        Loading...
      </div>
    )
  }

  if (!eventId) {
    return (
      <div className={styles.noEvent}>
        Select an event to view tickets
      </div>
    )
  }

  return (
    <div>
      {actionError && (
        <div className={styles.errorBanner}>
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className={styles.errorBannerButton}>×</button>
        </div>
      )}
      {/* Back button */}
      <div className={styles.backButtonContainer}>
        <button
          onClick={() => navigate(-1)}
          className={styles.backButton}
        >
          ← Back
        </button>
      </div>

      <div className={styles.header}>
        <h2 className={styles.eventTitle}>Tickets</h2>
        <p className={styles.eventSubtitle}>
          {event?.title || 'Select an event'}
        </p>
      </div>

      {/* Security notice */}
      <div className={styles.securityNotice}>
        <div className={styles.securityBox}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.securityIcon}>
            <rect x="3" y="11" width="18" height="11" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Payment handled securely by <strong>Ticket Tailor</strong> — money lands in the organiser's account, never ours.</span>
        </div>
      </div>

      <HR />

      {/* Quantity picker */}
      <div className={styles.quantitySection}>
        <div className={styles.ticketInfo}>
          <div className={styles.ticketName}>
            <div className={styles.ticketTitle}>General admission</div>
            <div className={styles.ticketDetails}>{ticketPrice} · unreserved</div>
          </div>
          <div className={styles.quantityControls}>
            <button onClick={() => setQty(Math.max(0, qty - 1))} className={styles.quantityButton}>−</button>
            <span className={styles.quantityDisplay}>{qty}</span>
            <button onClick={() => setQty(qty + 1)} className={styles.quantityButton}>＋</button>
          </div>
        </div>
      </div>

      {/* Price breakdown */}
      <div className={styles.priceBreakdown}>
        <div className={styles.priceRow}>
          <span className={styles.priceLabel}>Ticket</span>
          <span>{ticketPrice}</span>
        </div>
        <div className={styles.priceRow}>
          <span className={styles.priceLabel}>Community fee</span>
          <span>{parseFloat(feePerTicket) > 0 ? `$${Number(feePerTicket).toFixed(2)}` : 'Waived'}</span>
        </div>
        <p className={styles.feeInfo}>
          {parseFloat(feePerTicket) > 0
            ? "OnIsland's community fee. Ticket Tailor's own fee still applies at their page."
            : "Waived while we get going — OnIsland takes nothing per ticket right now. Ticket Tailor's own fee still applies at their page."}
        </p>
      </div>

      {/* Purchase button */}
      <div className={styles.purchaseSection}>
        <Button className={`${styles.purchaseButton} btn-block`} disabled={qty === 0 || purchasing} onClick={handlePurchase}>
          {purchasing ? 'Processing...' : 'Continue to Ticket Tailor ›'}
        </Button>
        <p className={styles.purchaseText}>
          You'll finish on Ticket Tailor's secure page. OnIsland just carries you there.
        </p>
      </div>

      {/* My tickets section */}
      <HR />
      {myTickets.length > 0 && (
        <div className={styles.myTicketsSection}>
          <div className={styles.ticketsHeader}>{isDoorStaff ? 'Attendees' : 'My Tickets'}</div>
          {myTickets.map((t) => {
            const canCheckIn = t.status === 'valid' && (user?.role === 'admin' || user?.role === 'volunteer')
            return (
              <div key={t.id} className={styles.ticketItem}>
                <div className={styles.ticketInfoRow}>
                  <div>
                    <div className={styles.ticketNameDisplay}>
                      {t.member_name || (isDoorStaff ? 'Unknown' : 'You')}
                    </div>
                    <div className={styles.ticketStatusText}>
                      {t.checked_in_at
                        ? `Checked in ${new Date(t.checked_in_at).toLocaleDateString()} by ${t.checked_in_by_name ?? 'unknown'}`
                        : t.status === 'valid' ? 'Valid ticket'
                        : t.status === 'pending' ? 'Awaiting payment'
                        : t.status === 'refunded' ? 'Refunded'
                        : t.status === 'cancelled' ? 'Cancelled'
                        : t.status}
                    </div>
                  </div>
                  <div className={styles.ticketStatus}>
                    {canCheckIn && (
                      <Button
                        className={styles.checkInButton}
                        onClick={async () => {
                          setActionError(null)
                          try {
                            const res = await api.tickets.checkIn(t.id)
                            setMyTickets(prev => prev.map(ticket =>
                              ticket.id === t.id
                                ? { ...ticket, status: 'checked_in', checked_in_at: res.checked_in_at }
                                : ticket
                            ))
                          } catch (err) {
                            console.error('Failed to check in ticket:', err)
                            setActionError('Failed to check in ticket. Please try again.')
                          }
                        }}
                      >
                        Check In
                      </Button>
                    )}
                    <Tag variant={t.status === 'checked_in' ? 'accent' : 'neutral'}>{t.status}</Tag>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
